/* global Word */
/**
 * TableBuilder.ts
 *
 * Static helpers for Word table construction.
 * All methods operate on Word API objects — no stored state.
 */

import { logger } from '../../core/logging/index';

export class TableBuilder {

  // ─── Border helpers ───────────────────────────────────────────────────────

  /** Removes all visible borders from a table. */
  static clearAllBorders(table: Word.Table): void {
    const none = Word.BorderType.none;
    table.getBorder(Word.BorderLocation.top).type    = none;
    table.getBorder(Word.BorderLocation.bottom).type = none;
    table.getBorder(Word.BorderLocation.left).type   = none;
    table.getBorder(Word.BorderLocation.right).type  = none;
    table.getBorder(Word.BorderLocation.insideHorizontal).type = none;
    table.getBorder(Word.BorderLocation.insideVertical).type   = none;
  }

  /** Removes all borders from a single cell. */
  static clearCellBorders(cell: Word.TableCell): void {
    const none = Word.BorderType.none;
    cell.getBorder(Word.BorderLocation.top).type    = none;
    cell.getBorder(Word.BorderLocation.bottom).type = none;
    cell.getBorder(Word.BorderLocation.left).type   = none;
    cell.getBorder(Word.BorderLocation.right).type  = none;
  }

  /** Sets a colored bottom border on a single cell. */
  static setCellBottomBorder(cell: Word.TableCell, colorHex: string): void {
    const border = cell.getBorder(Word.BorderLocation.bottom);
    border.type  = Word.BorderType.single;
    border.color = colorHex.startsWith('#') ? colorHex : `#${colorHex}`;
    border.width = 0.75;
  }

  // ─── Dimension helpers ────────────────────────────────────────────────────

  /**
   * Pins a table to a fixed width so it doesn't reflow when content changes.
   * @param widthPt — total table width in points
   */
  static lockWidth(table: Word.Table, widthPt: number): void {
    table.autoFitBehavior('FixedSize');
    table.width     = widthPt;
    table.alignment = Word.Alignment.left;
  }

  /**
   * Pins a single column to a fixed width.
   * @param widthPt — column width in points
   */
  static lockColumnWidth(cell: Word.TableCell, widthPt: number): void {
    cell.columnWidth = widthPt;
  }

  /** Sets the height of a row. */
  static setRowHeight(
    row: Word.TableRow,
    heightPt: number,
    rule: Word.RowHeightRule = Word.RowHeightRule.atLeast
  ): void {
    row.preferredHeight = heightPt;
    (row as any).heightRule = rule;
  }

  // ─── Cell padding ─────────────────────────────────────────────────────────

  /** Removes all internal padding from a cell. */
  static zeroCellPadding(cell: Word.TableCell): void {
    cell.setCellPadding(Word.CellPaddingLocation.top,    0);
    cell.setCellPadding(Word.CellPaddingLocation.bottom, 0);
    cell.setCellPadding(Word.CellPaddingLocation.left,   0);
    cell.setCellPadding(Word.CellPaddingLocation.right,  0);
  }

  // ─── Table indent ─────────────────────────────────────────────────────────

  /**
   * Applies a left indent to a table by injecting <w:tblInd> into the table's
   * OOXML properties, then replacing the table via Range.insertOoxml(replace).
   *
   * IMPORTANT: This invalidates the `table` JS reference. Call this LAST —
   * after all other table setup — and do not use the `table` object afterward.
   *
   * @param context   — active Word.RequestContext
   * @param table     — the table to indent (reference becomes stale after call)
   * @param leftIndentPt — indent in points (pass 0 or undefined to skip)
   */
  static async applyIndent(
    context: Word.RequestContext,
    table: Word.Table,
    leftIndentPt: number | undefined
  ): Promise<void> {
    if (!leftIndentPt || leftIndentPt <= 0) return;

    // Convert points → twips  (1 pt = 20 twips, Word's native unit for tblInd)
    const twips = Math.round(leftIndentPt * 20);

    try {
      // Get the OOXML for the table's full range
      const tableRange = table.getRange(Word.RangeLocation.whole);
      const ooxmlProxy = tableRange.getOoxml();
      await context.sync();

      let ooxml = ooxmlProxy.value;
      const indTag = `<w:tblInd w:w="${twips}" w:type="dxa"/>`;

      if (ooxml.includes('<w:tblInd')) {
        // Replace the existing tblInd element (handles both self-closing and separate closing tags)
        ooxml = ooxml.replace(/<w:tblInd[^>]*>(?:<\/w:tblInd>)?|<w:tblInd[^>]*\/>/g, indTag);
      } else if (/<w:tblPr[ >]/.test(ooxml)) {
        // tblPr matches either tag syntax — find end of opening tag and inject after
        ooxml = ooxml.replace(/(<w:tblPr[^>]*>)/, `$1${indTag}`);
      } else {
        // No tblPr at all — inject a minimal one before the first row
        ooxml = ooxml.replace(/(<w:tr[ >])/, `<w:tblPr>${indTag}</w:tblPr>$1`);
      }

      // Replace via Range.insertOoxml — this is the supported API for table replacement
      tableRange.insertOoxml(ooxml, Word.InsertLocation.replace);
      await context.sync();

      logger.info('TableBuilder.applyIndent: indent applied', { twips });
    } catch (err) {
      // Log and swallow — indent failure should not break the insertion
      logger.warn('TableBuilder.applyIndent: failed to apply indent', { err: String(err) });
    }
  }
}